import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ClientDto } from '../services/clientsService';
import { DiseaseDto, diseasesApi } from '../services/settingsService';
import { hexToRgba } from '../utils/colorUtils';
import CollapsibleSection from './common/CollapsibleSection';
import SecretaryManagementSection from './secretary/SecretaryManagementSection';
import { useTenantConfig } from '../contexts/TenantConfigContext';

interface ClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: ClientDto | null;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  direction: rtl;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-heavy);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  direction: rtl;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  
  &:hover {
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 8px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const InfoField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  text-transform: uppercase;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
`;

const StatusBadge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: var(--border-radius-md);
  font-size: 12px;
  font-weight: 600;
  background: ${props => hexToRgba(props.color, 0.12)};
  color: ${props => props.color};
  border: 1px solid ${props => hexToRgba(props.color, 0.25)};
  width: fit-content;
`;

const MedicalInfoCard = styled.div`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 16px;
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MedicalInfoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const DiseaseName = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const PrimaryBadge = styled.span`
  background: var(--success-color);
  color: white;
  padding: 2px 8px;
  border-radius: var(--border-radius-sm);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
`;

const DiseaseCategory = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const MedicalDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 12px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: var(--text-secondary);
  font-style: italic;
`;

/* ============================================================
   תת-סעיף (כותרת קטנה בתוך CollapsibleSection)
   ============================================================ */
const SubSectionHeader = styled.h4`
  margin: 16px 0 10px 0;
  padding-bottom: 6px;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  border-bottom: 1px dashed var(--border-color);
`;

/* ============================================================
   תצוגת אנשי קשר (קלפים)
   ============================================================ */
const ContactsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ContactCard = styled.div<{ $isPrimary?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border-radius: 6px;
  background: ${props => props.$isPrimary ? 'rgba(22, 163, 74, 0.06)' : 'var(--background-color, #f7f8fa)'};
  border-right: 3px solid ${props => props.$isPrimary ? '#16a34a' : 'var(--border-color)'};
`;

const ContactPhoneLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PrimaryStar = styled.span`
  color: #16a34a;
  font-size: 14px;
  line-height: 1;
`;

const ContactNumber = styled.span`
  font-size: 14px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--text-primary);
`;

const ContactLabelTag = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(0, 115, 234, 0.1);
  color: #0073ea;
  font-weight: 500;
`;

const ContactEmailLine = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

/* ============================================================
   תוויות מילולות לערכי enum
   ============================================================ */
const BENEFICIARY_LABEL: Record<string, string> = {
  adult: 'מבוגר',
  child: 'ילד',
  other: 'אחר',
};

const SUBMITTER_SOURCE_LABEL: Record<string, string> = {
  father: 'אבא',
  mother: 'אמא',
  spouse: 'בן/בת זוג',
  other: 'אחר',
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateOnly = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

const ClientModal: React.FC<ClientModalProps> = ({
  isOpen,
  onClose,
  client
}) => {
  const { config } = useTenantConfig();
  const bankStandingOrderEnabled = !!config?.bankStandingOrderEnabled;
  const [, setDiseases] = useState<DiseaseDto[]>([]);

  useEffect(() => {
    const loadDiseases = async () => {
      try {
        const diseasesData = await diseasesApi.getAll();
        setDiseases(diseasesData);
      } catch (error) {
        console.error('Error loading diseases:', error);
      }
    };

    if (isOpen) {
      loadDiseases();
    }
  }, [isOpen]);

  // Track where mousedown started to prevent closing when selecting text
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  if (!client) return null;

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && mouseDownTargetRef.current === e.currentTarget) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  return (
    <ModalOverlay $isOpen={isOpen} onMouseDown={handleMouseDown} onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>פרטי לקוח - {client.fullName}</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {/* Personal Information */}
          <Section>
            <SectionTitle>פרטים אישיים</SectionTitle>
            <InfoGrid>
              <InfoField>
                <InfoLabel>שם מלא</InfoLabel>
                <InfoValue>{client.fullName}</InfoValue>
              </InfoField>

              <InfoField>
                <InfoLabel>תעודת זהות</InfoLabel>
                <InfoValue>{client.idNumber}</InfoValue>
              </InfoField>

              <InfoField>
                <InfoLabel>דוא״ל</InfoLabel>
                <InfoValue>{client.email || '-'}</InfoValue>
              </InfoField>

              <InfoField>
                <InfoLabel>תאריך לידה</InfoLabel>
                <InfoValue>
                  {client.birthDate ? formatDateOnly(client.birthDate) : '-'}
                </InfoValue>
              </InfoField>
            </InfoGrid>
          </Section>

          {/* ============================================================
              אנשי קשר — מקטע מתכווץ
              ============================================================ */}
          <Section>
            <CollapsibleSection title="אנשי קשר" defaultOpen={true}>
              {client.contacts && client.contacts.length > 0 ? (
                <ContactsList>
                  {client.contacts.map((contact, index) => (
                    <ContactCard key={contact.id || index} $isPrimary={contact.isPrimary}>
                      <ContactPhoneLine>
                        {contact.isPrimary && <PrimaryStar title="איש קשר ראשי">★</PrimaryStar>}
                        <ContactNumber dir="ltr">{contact.number}</ContactNumber>
                        {contact.label && <ContactLabelTag>{contact.label}</ContactLabelTag>}
                      </ContactPhoneLine>
                      {contact.email && (
                        <ContactEmailLine dir="ltr">
                          ✉ {contact.email}
                        </ContactEmailLine>
                      )}
                    </ContactCard>
                  ))}
                </ContactsList>
              ) : (
                <InfoValue>{client.phone || '-'}</InfoValue>
              )}
            </CollapsibleSection>
          </Section>

          {/* ============================================================
              פרטים נוספים — מקטע מתכווץ עם סוג מוטב + הורים / בן זוג / מגיש
              ============================================================ */}
          <Section>
            <CollapsibleSection title="פרטים נוספים" defaultOpen={false}>
              {/* סוג מוטב */}
              {client.beneficiaryType && (
                <InfoGrid>
                  <InfoField>
                    <InfoLabel>סוג מוטב</InfoLabel>
                    <InfoValue>{BENEFICIARY_LABEL[client.beneficiaryType] || client.beneficiaryType}</InfoValue>
                  </InfoField>
                </InfoGrid>
              )}

              {/* פרטי האב */}
              {(client.fatherName || client.fatherIdNumber || client.fatherAddress) && (
                <>
                  <SubSectionHeader>פרטי האב</SubSectionHeader>
                  <InfoGrid>
                    <InfoField>
                      <InfoLabel>שם</InfoLabel>
                      <InfoValue>{client.fatherName || '-'}</InfoValue>
                    </InfoField>
                    <InfoField>
                      <InfoLabel>תעודת זהות</InfoLabel>
                      <InfoValue>
                        {client.fatherIdNumber ? (
                          <>
                            <span dir="ltr">{client.fatherIdNumber}</span>
                            {client.isFatherForeignResident && (
                              <span style={{ marginRight: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                                (תושב חו"ל)
                              </span>
                            )}
                          </>
                        ) : '-'}
                      </InfoValue>
                    </InfoField>
                    <InfoField>
                      <InfoLabel>כתובת</InfoLabel>
                      <InfoValue>
                        {[client.fatherAddress, client.fatherCity].filter(Boolean).join(', ') || '-'}
                      </InfoValue>
                    </InfoField>
                    <InfoField>
                      <InfoLabel>תאריך לידה</InfoLabel>
                      <InfoValue>{formatDateOnly(client.fatherBirthDate)}</InfoValue>
                    </InfoField>
                  </InfoGrid>
                </>
              )}

              {/* פרטי האם */}
              {(client.motherName || client.motherIdNumber || client.motherAddress) && (
                <>
                  <SubSectionHeader>פרטי האם</SubSectionHeader>
                  <InfoGrid>
                    <InfoField>
                      <InfoLabel>שם</InfoLabel>
                      <InfoValue>{client.motherName || '-'}</InfoValue>
                    </InfoField>
                    <InfoField>
                      <InfoLabel>תעודת זהות</InfoLabel>
                      <InfoValue>
                        {client.motherIdNumber ? (
                          <>
                            <span dir="ltr">{client.motherIdNumber}</span>
                            {client.isMotherForeignResident && (
                              <span style={{ marginRight: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                                (תושב חו"ל)
                              </span>
                            )}
                          </>
                        ) : '-'}
                      </InfoValue>
                    </InfoField>
                    <InfoField>
                      <InfoLabel>כתובת</InfoLabel>
                      <InfoValue>
                        {[client.motherAddress, client.motherCity].filter(Boolean).join(', ') || '-'}
                      </InfoValue>
                    </InfoField>
                    <InfoField>
                      <InfoLabel>תאריך לידה</InfoLabel>
                      <InfoValue>{formatDateOnly(client.motherBirthDate)}</InfoValue>
                    </InfoField>
                  </InfoGrid>
                </>
              )}

              {/* פרטי בן/בת זוג */}
              {(client.spouseName || client.spouseIdNumber || client.spouseAddress) && (
                <>
                  <SubSectionHeader>פרטי בן/בת זוג</SubSectionHeader>
                  <InfoGrid>
                    <InfoField>
                      <InfoLabel>שם</InfoLabel>
                      <InfoValue>{client.spouseName || '-'}</InfoValue>
                    </InfoField>
                    <InfoField>
                      <InfoLabel>תעודת זהות</InfoLabel>
                      <InfoValue>
                        {client.spouseIdNumber ? (
                          <>
                            <span dir="ltr">{client.spouseIdNumber}</span>
                            {client.isSpouseForeignResident && (
                              <span style={{ marginRight: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
                                (תושב חו"ל)
                              </span>
                            )}
                          </>
                        ) : '-'}
                      </InfoValue>
                    </InfoField>
                    <InfoField>
                      <InfoLabel>כתובת</InfoLabel>
                      <InfoValue>
                        {[client.spouseAddress, client.spouseCity].filter(Boolean).join(', ') || '-'}
                      </InfoValue>
                    </InfoField>
                    <InfoField>
                      <InfoLabel>תאריך לידה</InfoLabel>
                      <InfoValue>{formatDateOnly(client.spouseBirthDate)}</InfoValue>
                    </InfoField>
                  </InfoGrid>
                </>
              )}

              {/* מגיש הבקשה */}
              {(client.submitterName || client.submitterIdNumber) && (
                <>
                  <SubSectionHeader>מגיש הבקשה</SubSectionHeader>
                  <InfoGrid>
                    {client.submitterSourceType && (
                      <InfoField>
                        <InfoLabel>מקור פרטי המגיש</InfoLabel>
                        <InfoValue>
                          {SUBMITTER_SOURCE_LABEL[client.submitterSourceType] || client.submitterSourceType}
                        </InfoValue>
                      </InfoField>
                    )}
                    <InfoField>
                      <InfoLabel>שם המגיש</InfoLabel>
                      <InfoValue>{client.submitterName || '-'}</InfoValue>
                    </InfoField>
                    <InfoField>
                      <InfoLabel>תעודת זהות</InfoLabel>
                      <InfoValue>
                        {client.submitterIdNumber ? <span dir="ltr">{client.submitterIdNumber}</span> : '-'}
                      </InfoValue>
                    </InfoField>
                    <InfoField>
                      <InfoLabel>קרבת משפחה</InfoLabel>
                      <InfoValue>{client.submitterRelationship || '-'}</InfoValue>
                    </InfoField>
                    <InfoField>
                      <InfoLabel>כתובת</InfoLabel>
                      <InfoValue>
                        {[client.submitterAddress, client.submitterCity].filter(Boolean).join(', ') || '-'}
                      </InfoValue>
                    </InfoField>
                  </InfoGrid>
                </>
              )}
            </CollapsibleSection>
          </Section>

          {/* פרטי חשבון בנק — מוצג תמיד, מכווץ כברירת מחדל */}
          <Section>
            <CollapsibleSection
              title="פרטי חשבון בנק להעברת קצבה"
              defaultOpen={false}
            >
              <SubSectionHeader>פרטי מקבל הקצבה</SubSectionHeader>
              <InfoGrid>
                <InfoField>
                  <InfoLabel>שם מוטב</InfoLabel>
                  <InfoValue>{client.beneficiaryName || '-'}</InfoValue>
                </InfoField>
                <InfoField>
                  <InfoLabel>ת.ז. מוטב</InfoLabel>
                  <InfoValue>
                    {client.beneficiaryIdNumber ? <span dir="ltr">{client.beneficiaryIdNumber}</span> : '-'}
                  </InfoValue>
                </InfoField>
                <InfoField>
                  <InfoLabel>קוד בנק</InfoLabel>
                  <InfoValue>
                    {client.bankCode ? <span dir="ltr">{client.bankCode}</span> : '-'}
                  </InfoValue>
                </InfoField>
                <InfoField>
                  <InfoLabel>מספר סניף</InfoLabel>
                  <InfoValue>
                    {client.branchNumber ? <span dir="ltr">{client.branchNumber}</span> : '-'}
                  </InfoValue>
                </InfoField>
                <InfoField>
                  <InfoLabel>מספר חשבון</InfoLabel>
                  <InfoValue>
                    {client.accountNumber ? <span dir="ltr">{client.accountNumber}</span> : '-'}
                  </InfoValue>
                </InfoField>
              </InfoGrid>

              {bankStandingOrderEnabled && (
              <>
              <SubSectionHeader>פרטי משלם הקצבה</SubSectionHeader>
              <InfoGrid>
                <InfoField>
                  <InfoLabel>שם משלם הקצבה</InfoLabel>
                  <InfoValue>{client.payerName || '-'}</InfoValue>
                </InfoField>
                <InfoField>
                  <InfoLabel>ת.ז. משלם הקצבה</InfoLabel>
                  <InfoValue>
                    {client.payerIdNumber ? (
                      <span dir="ltr">
                        {client.payerIdNumber}
                        {client.isPayerForeignResident && ' (תושב חו"ל)'}
                      </span>
                    ) : '-'}
                  </InfoValue>
                </InfoField>
              </InfoGrid>
              </>
              )}
            </CollapsibleSection>
          </Section>

          {/* Case Management */}
          <Section>
            <SectionTitle>ניהול תיק</SectionTitle>
            <InfoGrid>
              <InfoField>
                <InfoLabel>מנהל תיק</InfoLabel>
                <InfoValue>{client.assignedCaseManagerName || 'לא מוקצה'}</InfoValue>
              </InfoField>
              
              <InfoField>
                <InfoLabel>סטטוס נוכחי</InfoLabel>
                {client.currentStatusName && client.currentStatusColor ? (
                  <StatusBadge color={client.currentStatusColor}>
                    {client.currentStatusName}
                  </StatusBadge>
                ) : (
                  <InfoValue>-</InfoValue>
                )}
              </InfoField>
              
              <InfoField>
                <InfoLabel>תאריך שינוי סטטוס אחרון</InfoLabel>
                <InfoValue>{formatDate(client.lastStatusChangeAt)}</InfoValue>
              </InfoField>
              
              <InfoField>
                <InfoLabel>תאריך תחילת טיפול</InfoLabel>
                <InfoValue>{formatDateOnly(client.treatmentStartDate)}</InfoValue>
              </InfoField>
              
              <InfoField>
                <InfoLabel>ימים בטיפול</InfoLabel>
                <InfoValue>
                  {client.daysInTreatment > 0 ? `${client.daysInTreatment} ימים` : '-'}
                </InfoValue>
              </InfoField>
            </InfoGrid>
          </Section>

          {/* Contract Information */}
          <Section>
            <SectionTitle>פרטי חוזה</SectionTitle>
            <InfoGrid>
              <InfoField>
                <InfoLabel>נציג מכירות מקורי</InfoLabel>
                <InfoValue>{client.salesRepName || '-'}</InfoValue>
              </InfoField>
              
              <InfoField>
                <InfoLabel>תאריך חתימה</InfoLabel>
                <InfoValue>{formatDate(client.contractSignedDate)}</InfoValue>
              </InfoField>
              
              <InfoField>
                <InfoLabel>נוצר על ידי</InfoLabel>
                <InfoValue>{client.createdByName}</InfoValue>
              </InfoField>
              
              <InfoField>
                <InfoLabel>תאריך יצירה</InfoLabel>
                <InfoValue>{formatDate(client.createdAt)}</InfoValue>
              </InfoField>
              
              {client.updatedAt && (
                <InfoField>
                  <InfoLabel>עודכן לאחרונה</InfoLabel>
                  <InfoValue>{formatDate(client.updatedAt)}</InfoValue>
                </InfoField>
              )}
            </InfoGrid>
          </Section>

          {/* Medical Information */}
          <Section>
            <SectionTitle>מידע רפואי</SectionTitle>
            {client.medicalInfo && client.medicalInfo.length > 0 ? (
              client.medicalInfo.map((medical) => (
                <MedicalInfoCard key={medical.id}>
                  <MedicalInfoHeader>
                    <div>
                      <DiseaseName>{medical.diseaseName}</DiseaseName>
                      <DiseaseCategory>{medical.diseaseCategoryName}</DiseaseCategory>
                    </div>
                    {medical.isPrimary && <PrimaryBadge>עיקרית</PrimaryBadge>}
                  </MedicalInfoHeader>
                  
                  <MedicalDetails>
                    {medical.diagnosisDate && (
                      <InfoField>
                        <InfoLabel>תאריך אבחון</InfoLabel>
                        <InfoValue>{formatDateOnly(medical.diagnosisDate)}</InfoValue>
                      </InfoField>
                    )}
                    
                    {medical.notes && (
                      <InfoField>
                        <InfoLabel>הערות</InfoLabel>
                        <InfoValue>{medical.notes}</InfoValue>
                      </InfoField>
                    )}
                  </MedicalDetails>
                </MedicalInfoCard>
              ))
            ) : (
              <EmptyState>
                לא הוגדר מידע רפואי עבור לקוח זה
              </EmptyState>
            )}
          </Section>

          <Section>
            <SecretaryManagementSection
              entityType="client"
              entityId={client.id}
            />
          </Section>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ClientModal;